// This is the dialog the user uses to enter the deposit information
// of a user.  They can load funds via etransfer, or cash and then
// they can put notes in their order if needed.
import React, { useState } from "react";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import "date-fns";
import CommunityMemberTransaction from "../../../functions/Firestore/Wallet/CommunityMemberTransaction.js";
import TransactionHistoryDialog from "./TransactionHistoryDialog.jsx";
import WalletLoadDetails from "./PaymentEntry/components/WalletLoadDetails.jsx";
import handleDepositChange from "./PaymentEntry/utils/handleDepositChange.js";
import handleNotesChange from "./PaymentEntry/utils/handleNotesChange.js";

export default function AddFundsToMemberWallet({
  communityWallets,
  index,
  setCommunityWallets,
  wallet,
  locations,
}) {
  const [open, setOpen] = useState(false);
  // Stores the order notes that will be used by the user to update the order notes.
  const [orderNote, setOrderNote] = useState("");
  const [locationError, setLocationError] = useState(false);
  const [collectedDateError, setCollectedDateError] = useState(false);

  // Create a default date for in the format YYYY-MM-DD to be used to set default
  // dates for deposits and refunds that do no have dates.
  let defaultDateTemp = new Date();

  const y = defaultDateTemp.getFullYear();
  const m = defaultDateTemp.getMonth() + 1;
  const d = defaultDateTemp.getDate();

  defaultDateTemp =
    "" + y + "-" + (m < 10 ? "0" : "") + m + "-" + (d < 10 ? "0" : "") + d;

  // Contains the deposit info
  const [deposit, setDeposit] = useState({
    method: "etransfer",
    amount: 0.0,
    date: defaultDateTemp,
    location: "", // add this to track cash location
    collectedDate: "", // collected on date for cash
  });

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  // When the user saves their changes then this calls the functions to save to
  // the database.
  const handleSave = async (e) => {
    e.preventDefault();

    // Validate required fields for cash
    if (deposit.method === "cash") {
      let hasError = false;
      // The location date isn't set
      if (!deposit.location) {
        setLocationError(true);
        hasError = true;
      }
      // The collected date isn't set
      if (!deposit.collectedDate) {
        setCollectedDateError(true);
        hasError = true;
      }

      if (hasError) return; // stop saving if either is missing
    }

    // For cash deposits, record the pickup location and collected-on date in
    // the description so there is a clear record of where/when it was collected.
    let description = orderNote;
    if (deposit.method === "cash") {
      description = `Cash collected at ${deposit.location} on ${deposit.collectedDate}`;
      if (orderNote) {
        description += ` - ${orderNote}`;
      }
    }

    const communityWalletsTemp = communityWallets;
    const updatedWallet = await CommunityMemberTransaction(
      deposit.amount,
      { ...communityWallets[index] },
      description,
      deposit.date,
      deposit.method,
      deposit.location,
      deposit.collectedDate, // pass collected date for cash
    );
    communityWalletsTemp[index] = updatedWallet;
    setCommunityWallets([...communityWalletsTemp]);
    setOpen(false);
    setDeposit({
      method: "etransfer",
      amount: 0.0,
      date: defaultDateTemp,
      location: "",
      collectedDate: "",
    });
  };

  return (
    <div>
      <Button size="small" color="primary" onClick={handleClickOpen}>
        LOAD WALLET
      </Button>
      <Dialog
        onClose={handleClose}
        aria-labelledby="simple-dialog-title"
        open={open}
      >
        <DialogTitle id="simple-dialog-title">
          Loading Funds on to {communityWallets[index].firstName}{" "}
          {communityWallets[index].lastName}'s Wallet
        </DialogTitle>
        <DialogContent>
          <TransactionHistoryDialog
            buttonSize="small"
            balance={parseFloat(wallet.Total)}
            transactionHistory={wallet.transactionHistory || []}
          />

          <form onSubmit={handleSave}>
            <WalletLoadDetails
              deposit={deposit}
              setDeposit={setDeposit}
              handleDepositChange={handleDepositChange}
            />

            {/* Show location dropdown only when cash is selected */}
            {deposit.method === "cash" && (
              <span>
                <FormControl fullWidth margin="normal" variant="outlined">
                  <InputLabel id="cash-location-label">
                    Select Location
                  </InputLabel>
                  <Select
                    labelId="cash-location-label"
                    value={deposit.location}
                    onChange={(e) => {
                      setDeposit((prev) => ({
                        ...prev,
                        location: e.target.value,
                      }));
                      setLocationError(false);
                    }}
                    label="Select Location"
                  >
                    {locations.map((loc) => (
                      <MenuItem key={loc} value={loc}>
                        {loc}
                      </MenuItem>
                    ))}
                  </Select>
                  {locationError && (
                    <p style={{ color: "red", margin: "0.25rem 0 0 0" }}>
                      Please select a location for cash deposits
                    </p>
                  )}
                </FormControl>
                <TextField
                  label="Collected On"
                  type="date"
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  value={deposit.collectedDate}
                  onChange={(e) => {
                    const value = e?.target?.value;
                    if (!value) return;
                    setDeposit((prev) => ({
                      ...prev,
                      collectedDate: value,
                    }));
                    setCollectedDateError(false);
                  }}
                  InputLabelProps={{
                    shrink: true, // ensures label doesn’t overlap with the date
                  }}
                  error={collectedDateError}
                  helperText={
                    collectedDateError ? "Please select a collection date" : ""
                  }
                />
              </span>
            )}

            <span>
              <h5>Order Notes</h5>
              <TextField
                label="Notes"
                fullWidth
                multiline
                minRows={4}
                variant="outlined"
                value={orderNote}
                onChange={(e) => handleNotesChange(e, setOrderNote)}
              />
            </span>
            <DialogActions>
              <Button onClick={handleClose} color="primary">
                Cancel
              </Button>
              <Button type="submit" color="primary">
                Save
              </Button>
            </DialogActions>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
